import { Injectable, OnDestroy } from '@angular/core';
import { CalculateResult, CalculatorItem, CalculatorOccFormData, CustomProduct } from '@product/const';
import { ProductCalculatorService } from '@product/data-access';
import { CurrentProductService } from '@spartacus/storefront';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { filter, map, switchMap, take, tap } from 'rxjs/operators';

@Injectable()
export class ActiveProductCalculatorService implements OnDestroy {
  activeCalculatorId$: BehaviorSubject<string> = new BehaviorSubject('');
  calculateResults$: Observable<CalculateResult[]>;
  product$: Observable<CustomProduct | null>;
  calculators$: Observable<CalculatorItem[]>;
  isAddingToCart$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  private subscription = new Subscription();

  constructor(
    private productCalculatorService: ProductCalculatorService,
    private currentProductService: CurrentProductService,
  ) {
    this.product$ = <Observable<CustomProduct | null>>this.currentProductService.getProduct();

    this.calculators$ = this.product$.pipe(
      switchMap((product: CustomProduct | null) => {
        if (!product) {
          return [];
        }

        if (product.stock.stockLevelStatus === 'outOfStock') {
          return [];
        }

        return this.productCalculatorService.getCalculators(product.code).pipe(
          tap((calcs) => {
            if (!calcs) {
              this.productCalculatorService.loadCalculators(product.code);
            }
          }),
          filter(Boolean),
          tap((calculators: CalculatorItem[]) => {
            if (!this.activeCalculatorId$.value) {
              this.activeCalculatorId$.next(calculators[0]?.code);
            }
          }),
        );
      }),
    );
  }

  getActiveCalculator(): Observable<CalculatorItem | undefined> {
    return combineLatest({
      activeCalculatorId: this.activeCalculatorId$,
      calculators: this.calculators$.pipe(filter(Boolean)),
    }).pipe(
      map(({ calculators, activeCalculatorId }) =>
        calculators.find((calculator) => calculator.code === activeCalculatorId),
      ),
    );
  }

  getPrice(result: CalculateResult): Observable<number> {
    return this.product$.pipe(
      map((product: CustomProduct | null) => {
        const value = product?.unitPrices?.find((price) => price?.unit?.code === result?.unit?.code)?.price?.value;

        if (!value) {
          return 0;
        }

        return value * result.value;
      }),
    );
  }

  setActiveCalculator(code: string): void {
    this.activeCalculatorId$.next(code);
    this.productCalculatorService.resetResult();
  }

  calculate(formdata: CalculatorOccFormData[]): void {
    this.product$.pipe(take(1), filter(Boolean)).subscribe((product: CustomProduct) => {
      this.productCalculatorService.calculate(this.activeCalculatorId$.value, product.code, formdata);
    });
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this.productCalculatorService.resetResult();
  }
}
